// frontend/src/utils/qualityRules.js
/**
 * Sprint 14 — Catálogo de reglas de calidad de DatiaFlow (S4).
 *
 * FUENTE ÚNICA DE VERDAD para los tipos de quality rules.
 * Los IDs deben coincidir exactamente con:
 *   - Backend: _evaluate_single_rule() en services/data_rules.py
 *   - i18n:    data_rules.rule_* en locales/{es,en,pt}.json
 *
 * A diferencia de transformRules.js (pipeline secuencial de mutaciones),
 * las quality rules son validaciones PARALELAS sobre el valor final.
 * No mutan el dato — solo verifican que cumple la regla.
 *
 * Se evalúan POST-transformación, PRE-escritura.
 */

export const QUALITY_RULES = [
  {
    id: 'NOT_NULL',
    params: [],
    label: 'Campo obligatorio (no vacío)',
    description: 'El campo no puede estar vacío ni ser nulo',
    icon: 'AlertCircle',
  },
  {
    id: 'REGEX',
    params: ['pattern'],
    label: 'Formato válido (expresión regular)',
    description: 'El valor debe coincidir con un patrón específico',
    icon: 'Regex',
  },
  {
    id: 'RANGE',
    params: ['min', 'max'],
    label: 'Rango numérico',
    description: 'El valor numérico debe estar dentro del rango definido',
    icon: 'ArrowLeftRight',
  },
  {
    id: 'ALLOWED_VALUES',
    params: ['values'],
    label: 'Solo valores permitidos',
    description: 'El valor debe ser uno de una lista cerrada de opciones',
    icon: 'ListChecks',
  },
  {
    id: 'UNIQUE',
    params: [],
    label: 'Sin duplicados',
    description: 'No puede haber valores repetidos en esta columna',
    icon: 'Fingerprint',
  },
  {
    id: 'MIN_LENGTH',
    params: ['length'],
    label: 'Longitud mínima',
    description: 'El texto debe tener al menos N caracteres',
    icon: 'MoveHorizontal',
  },
  {
    id: 'MAX_LENGTH',
    params: ['length'],
    label: 'Longitud máxima',
    description: 'El texto no puede exceder N caracteres',
    icon: 'MoveHorizontal',
  },
];

/**
 * Presets de regex universales (no dependen de país).
 * Los presets por país se cargan desde GET /catalogs/validation-presets.
 */
export const UNIVERSAL_REGEX_PRESETS = [
  {
    id: 'email',
    pattern: '^[\\w.+-]+@[\\w-]+\\.[a-zA-Z]{2,}$',
    label: 'Email',
    labelEn: 'Email',
    labelPt: 'Email',
  },
  {
    id: 'url',
    pattern: '^https?:\\/\\/[\\w.-]+\\.[a-zA-Z]{2,}',
    label: 'URL (http/https)',
    labelEn: 'URL (http/https)',
    labelPt: 'URL (http/https)',
  },
  {
    id: 'numeric',
    pattern: '^-?\\d+(\\.\\d+)?$',
    label: 'Numérico (entero o decimal)',
    labelEn: 'Numeric (integer or decimal)',
    labelPt: 'Numérico (inteiro ou decimal)',
  },
  {
    id: 'alphanumeric',
    pattern: '^[A-Za-z0-9]+$',
    label: 'Alfanumérico (sin espacios)',
    labelEn: 'Alphanumeric (no spaces)',
    labelPt: 'Alfanumérico (sem espaços)',
  },
  {
    id: 'phone_international',
    pattern: '^\\+?\\d{7,15}$',
    label: 'Teléfono internacional',
    labelEn: 'International phone',
    labelPt: 'Telefone internacional',
  },
  {
    id: 'uuid',
    pattern: '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$',
    label: 'UUID',
    labelEn: 'UUID',
    labelPt: 'UUID',
  },
  {
    id: 'ipv4',
    pattern: '^((25[0-5]|2[0-4]\\d|[01]?\\d\\d?)\\.){3}(25[0-5]|2[0-4]\\d|[01]?\\d\\d?)$',
    label: 'Dirección IPv4',
    labelEn: 'IPv4 address',
    labelPt: 'Endereço IPv4',
  },
];

/**
 * Violation modes.
 */
export const VIOLATION_MODES = [
  { value: 'warn', label: 'Advertir (registrar pero escribir)', icon: 'AlertTriangle' },
  { value: 'block', label: 'Bloquear (no escribir el registro)', icon: 'ShieldBan' },
];

/**
 * Helper: obtener regla por ID.
 */
export function getQualityRuleById(id) {
  return QUALITY_RULES.find(r => r.id === id) || null;
}

/**
 * Helper: contar quality rules configuradas en un mapping.
 */
export function countQualityRules(mappings) {
  if (!Array.isArray(mappings)) return 0;
  let count = 0;
  for (const m of mappings) {
    const rules = m?.qualityRules;
    if (Array.isArray(rules)) count += rules.length;
  }
  return count;
}

/**
 * Helper: verificar si alguna regla tiene parámetros incompletos.
 */
export function getIncompleteQualityRules(mappings) {
  const incomplete = [];
  if (!Array.isArray(mappings)) return incomplete;

  for (const m of mappings) {
    const rules = m?.qualityRules;
    if (!Array.isArray(rules)) continue;

    const target = m?.target || '?';

    for (const rule of rules) {
      const ruleType = rule?.ruleType;
      const params = rule?.params || {};

      if (ruleType === 'REGEX' && !params.pattern) {
        incomplete.push({ target, ruleType, reason: 'missing_pattern' });
      }
      if (ruleType === 'RANGE' && params.min == null && params.max == null) {
        incomplete.push({ target, ruleType, reason: 'missing_range' });
      }
      if (ruleType === 'ALLOWED_VALUES') {
        const vals = params.values;
        if (!Array.isArray(vals) || vals.length === 0) {
          incomplete.push({ target, ruleType, reason: 'missing_values' });
        }
      }
      if (ruleType === 'MIN_LENGTH' && (!params.length || parseInt(params.length) <= 0)) {
        incomplete.push({ target, ruleType, reason: 'missing_length' });
      }
      if (ruleType === 'MAX_LENGTH' && (!params.length || parseInt(params.length) <= 0)) {
        incomplete.push({ target, ruleType, reason: 'missing_length' });
      }
    }
  }

  return incomplete;
}
